import React, {useState} from "react";
import {Formik, Form} from "formik";
import * as Yup from "yup";
import {toast} from "react-toastify";
import Modal from "../../../components/Modal";
import InputText from "../../../components/Inputs/InputText";
import Button from "../../../components/Button";
import SelectFieldComponent, {SelectOptionInterface} from "../../../components/SelectFieldComponent";
import AdminTable, {AdminTableColumn} from "../components/AdminTable";
import ConfirmDialog from "../components/ConfirmDialog";
import {AdminLink} from "../../../interfaces/admin";
import {
    useGetAdminLinksQuery,
    useUpdateAdminLinkMutation,
    useDeleteAdminLinkMutation,
} from "../../../storage/adminApi";
import {useGetUsersQuery} from "../../../storage/api";
import localStyles from "../styles.module.scss";

const AdminLinks = () => {
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(25);
    const [sortBy, setSortBy] = useState("id");
    const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
    const [filterUserId, setFilterUserId] = useState("");
    const [filterDateFrom, setFilterDateFrom] = useState("");
    const [filterDateTo, setFilterDateTo] = useState("");
    const [filterTracknumber, setFilterTracknumber] = useState("");
    const [editLink, setEditLink] = useState<AdminLink | null>(null);
    const [deleteTarget, setDeleteTarget] = useState<AdminLink | null>(null);

    const params: Record<string, any> = {page, per_page: pageSize, sort_by: sortBy, sort_order: sortOrder};
    if (filterUserId) params.user_id = filterUserId;
    if (filterDateFrom) params.date_from = filterDateFrom;
    if (filterDateTo) params.date_to = filterDateTo;
    if (filterTracknumber) params.tracknumber = filterTracknumber;

    const {data, isLoading} = useGetAdminLinksQuery(params);
    const {data: users} = useGetUsersQuery();
    const [updateLink] = useUpdateAdminLinkMutation();
    const [deleteLink] = useDeleteAdminLinkMutation();

    const userOptions: SelectOptionInterface[] = [
        {value: "", label: "All Users"},
        ...(users?.map((u) => ({value: String(u.id), label: u.username})) ?? []),
    ];

    const selectedUserOption = userOptions.find((o) => o.value === filterUserId) ?? null;

    const columns: AdminTableColumn[] = [
        {label: "ID", key: "id", sortable: true},
        {label: "User", key: "username", render: (value: string) => value ?? "-"},
        {label: "Tracknumber", key: "tracknumber"},
        {
            label: "Files",
            key: "files_count",
            render: (value: number) => value ?? 0,
        },
        {label: "Data Type", key: "data_type"},
        {label: "Created", key: "created_at", sortable: true},
        {
            label: "Actions",
            key: "actions",
            render: (_: any, row: AdminLink) => (
                <div className={localStyles.actionsCell}>
                    <Button
                        id={`edit-link-${row.id}`}
                        size="small"
                        background="bg-outline"
                        text="Edit"
                        click={(e) => {
                            e.stopPropagation();
                            setEditLink(row);
                        }}
                    />
                    <Button
                        id={`delete-link-${row.id}`}
                        size="small"
                        background="bg-danger"
                        text="Delete"
                        click={(e) => {
                            e.stopPropagation();
                            setDeleteTarget(row);
                        }}
                    />
                </div>
            ),
        },
    ];

    const handleUpdate = async (values: {tracknumber: string; url: string; data_type: string}) => {
        if (!editLink) return;
        try {
            await updateLink({linkId: editLink.id, data: values}).unwrap();
            toast.success("Link updated");
            setEditLink(null);
        } catch (e) {
            toast.error("Failed to update link");
        }
    };

    const handleDelete = async () => {
        if (!deleteTarget) return;
        try {
            await deleteLink(deleteTarget.id).unwrap();
            toast.success("Link deleted");
            setDeleteTarget(null);
        } catch (e) {
            toast.error("Failed to delete link");
        }
    };

    return (
        <div>
            <h4 className="fw-600" style={{marginBottom: 16}}>Links</h4>

            {/* Filter Bar */}
            <div className={localStyles.filterBar}>
                <div className={localStyles.filterGroup}>
                    <label>User</label>
                    <SelectFieldComponent
                        id="filter-user"
                        options={userOptions}
                        selectedValue={selectedUserOption}
                        placeholder="All Users"
                        handleSelect={(option: SelectOptionInterface) => {
                            setFilterUserId(option?.value ?? "");
                            setPage(1);
                        }}
                    />
                </div>
                <div className={localStyles.filterGroup}>
                    <label>From</label>
                    <InputText
                        id="filter-date-from"
                        type="date"
                        value={filterDateFrom}
                        handleChange={(value: string) => {
                            setFilterDateFrom(value);
                            setPage(1);
                        }}
                    />
                </div>
                <div className={localStyles.filterGroup}>
                    <label>To</label>
                    <InputText
                        id="filter-date-to"
                        type="date"
                        value={filterDateTo}
                        handleChange={(value: string) => {
                            setFilterDateTo(value);
                            setPage(1);
                        }}
                    />
                </div>
                <div className={localStyles.filterGroup}>
                    <label>Tracknumber</label>
                    <InputText
                        id="filter-tracknumber"
                        placeholder="Search..."
                        value={filterTracknumber}
                        handleChange={(value: string) => {
                            setFilterTracknumber(value);
                            setPage(1);
                        }}
                    />
                </div>
            </div>

            <AdminTable
                columns={columns}
                data={data?.items ?? []}
                total={data?.total ?? 0}
                page={page}
                pageSize={pageSize}
                onPageChange={setPage}
                onPageSizeChange={(size) => {
                    setPageSize(size);
                    setPage(1);
                }}
                loading={isLoading}
                sortBy={sortBy}
                sortOrder={sortOrder}
                onSortChange={(newSortBy, newSortOrder) => {
                    setSortBy(newSortBy);
                    setSortOrder(newSortOrder);
                    setPage(1);
                }}
            />

            {/* Edit Modal */}
            {editLink && (
                <Formik
                    initialValues={{
                        tracknumber: editLink.tracknumber ?? "",
                        url: editLink.url ?? "",
                        data_type: editLink.data_type ?? "",
                    }}
                    validationSchema={Yup.object().shape({
                        tracknumber: Yup.string(),
                        url: Yup.string(),
                        data_type: Yup.string(),
                    })}
                    onSubmit={handleUpdate}
                >
                    {({values, isSubmitting, setFieldValue, submitForm}) => (
                        <Modal
                            show
                            handleClose={() => setEditLink(null)}
                            width={480}
                            closeOut
                            element={
                                <div>
                                    <div className={localStyles.modalTitle}>Edit Link #{editLink.id}</div>
                                    <Form>
                                        <div className={localStyles.modalBody}>
                                            <InputText
                                                id="edit-tracknumber"
                                                labelText="Tracknumber"
                                                value={values.tracknumber}
                                                handleChange={(value: string) => setFieldValue("tracknumber", value)}
                                            />
                                            <InputText
                                                id="edit-url"
                                                labelText="URL"
                                                value={values.url}
                                                handleChange={(value: string) => setFieldValue("url", value)}
                                            />
                                            <InputText
                                                id="edit-data-type"
                                                labelText="Data Type"
                                                value={values.data_type}
                                                handleChange={(value: string) => setFieldValue("data_type", value)}
                                            />
                                        </div>
                                        <div className={localStyles.modalFooter}>
                                            <Button
                                                id="edit-link-cancel"
                                                size="small"
                                                background="bg-outline-grey"
                                                text="Cancel"
                                                click={() => setEditLink(null)}
                                            />
                                            <Button
                                                id="edit-link-submit"
                                                size="small"
                                                background="bg-primary"
                                                text="Update"
                                                type="submit"
                                                disabled={isSubmitting}
                                            />
                                        </div>
                                    </Form>
                                </div>
                            }
                        />
                    )}
                </Formik>
            )}

            <ConfirmDialog
                show={!!deleteTarget}
                title="Delete Link"
                message={`Are you sure you want to delete link #${deleteTarget?.id}?`}
                onConfirm={handleDelete}
                onCancel={() => setDeleteTarget(null)}
            />
        </div>
    );
};

export default <AdminLinks/>;
